import { Button } from "~/components/ui/button"
import { PlusIcon } from "lucide-react"
import { type ComponentProps, forwardRef, useState } from "react"

import { cn } from "~/lib/utils"
import { IssueLabel } from "./IssueLabel"

export function IssueLabelShowcase({
	labels,
	cap = 3,
	addButton = false,
	onRemove,
	truncateNamesTo,
	size = "default"
}: {
	labels: { id: string; name: string; color: string }[]
	cap?: number
	addButton?: boolean
	onRemove?: (labelId: string) => void
	truncateNamesTo?: number
	size?: "default" | "compact"
}) {
	const overflowing = !onRemove && labels.length > cap
	const [expanded, setExpanded] = useState(false)

	return (
		<div
			className={cn(
				"flex min-w-0 max-w-full items-center gap-1",
				size === "compact" ? "flex-nowrap" : "flex-wrap",
				overflowing && "justify-end"
			)}
		>
			{overflowing ? (
				<div
					role="group"
					tabIndex={0}
					aria-label={`${labels.length} labels: ${labels.map((label) => label.name).join(", ")}`}
					onPointerEnter={(event) => {
						if (event.pointerType === "mouse") setExpanded(true)
					}}
					onPointerLeave={(event) => {
						if (
							event.pointerType === "mouse" &&
							!event.currentTarget.contains(document.activeElement)
						)
							setExpanded(false)
					}}
					onFocus={() => setExpanded(true)}
					onBlur={(event) => {
						if (!event.currentTarget.contains(event.relatedTarget))
							setExpanded(false)
					}}
					onPointerDown={(event) => {
						if (event.pointerType !== "mouse") setExpanded((value) => !value)
					}}
					onKeyDown={(event) => {
						if (event.key === "Escape") {
							event.stopPropagation()
							setExpanded(false)
						}
						if (event.key === "Enter" || event.key === " ") {
							event.preventDefault()
							event.stopPropagation()
							setExpanded((value) => !value)
						}
					}}
					onClick={(event) => event.stopPropagation()}
					className={cn(
						"flex w-max min-w-0 items-center justify-end gap-1 rounded-full outline-none transition-[max-width] duration-200 ease-out motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-ring",
						expanded ? "max-w-[min(32rem,100%)]" : "max-w-[min(22rem,100%)]"
					)}
				>
					{labels.map((label) => (
						<span
							key={label.id}
							className="flex min-w-0 shrink overflow-hidden"
						>
							<IssueLabel
								label={label}
								size={size}
								truncateNameTo={truncateNamesTo}
							/>
						</span>
					))}
				</div>
			) : (
				labels.map((label) => (
					<IssueLabel
						key={label.id}
						size={size}
						label={label}
						onRemove={onRemove}
						truncateNameTo={truncateNamesTo}
					/>
				))
			)}
			{addButton && <IssueLabelAddButton />}
		</div>
	)
}

export const IssueLabelAddButton = forwardRef<
	HTMLButtonElement,
	ComponentProps<"button">
>(({ className, type = "button", ...props }, ref) => {
	return (
		<Button
			variant="outline"
			size="icon-sm"
			aria-label="Add label"
			ref={ref}
			type={type}
			className={className}
			{...props}
		>
			<PlusIcon className="size-4" />
		</Button>
	)
})

IssueLabelAddButton.displayName = "IssueLabelAddButton"
